import {
  IonButton,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonInput,
  IonSpinner,
  useIonToast,
} from "@ionic/react";
import { useEffect, useMemo, useState } from "react";
import Header from "src/components/Header";
import asignacionesCapacitador from "src/services/asignacionesCapacitador";

type PuntoServicio = {
  id: number;
  ps_nombre: string;
  ps_codigo: string;
  // Lo marca el backend para el Jefe de Operaciones: PS de su estructura.
  // Para el Capacitador Tecnico no viene y queda undefined.
  mi_estructura?: boolean;
};

type Asignacion = {
  id: number;
  ps_id: number;
  ps_nombre: string;
  ps_codigo: string;
  operario: string;
  legajo: string;
  cedula: string;
  estado: string;
  fecha_inicio: string;
  total_horas: string;
  lun_ent?: string;
  lun_sal?: string;
  mar_ent?: string;
  mar_sal?: string;
  mie_ent?: string;
  mie_sal?: string;
  jue_ent?: string;
  jue_sal?: string;
  vie_ent?: string;
  vie_sal?: string;
  sab_ent?: string;
  sab_sal?: string;
  dom_ent?: string;
  dom_sal?: string;
};

const AsignacionesCapacitador: React.FC = () => {
  const [segmento, setSegmento] = useState<"ps" | "persona">("ps");
  const [puntos, setPuntos] = useState<PuntoServicio[]>([]);
  const [filtroPs, setFiltroPs] = useState<string>("");
  const [psElegido, setPsElegido] = useState<PuntoServicio | null>(null);
  const [asignacionesPs, setAsignacionesPs] = useState<Asignacion[]>([]);
  const [asignacionesPersona, setAsignacionesPersona] = useState<Asignacion[]>([]);
  const [legajo, setLegajo] = useState<string>("");
  const [cargandoPuntos, setCargandoPuntos] = useState<boolean>(false);
  const [cargandoPs, setCargandoPs] = useState<boolean>(false);
  const [cargandoPersona, setCargandoPersona] = useState<boolean>(false);
  const [present] = useIonToast();

  useEffect(() => {
    const fetchPuntos = async () => {
      setCargandoPuntos(true);
      try {
        const res = await asignacionesCapacitador.listarPuntosServicio();
        setPuntos(res.data?.datos || []);
      } catch (error: any) {
        present({
          message: error?.response?.data?.error || "No se pudieron cargar los puntos de servicio",
          duration: 4000,
          color: "danger",
        });
      } finally {
        setCargandoPuntos(false);
      }
    };
    fetchPuntos();
  }, [present]);

  const diasConHorario = useMemo(
    () => (asig: Asignacion) => {
      const dias = [
        { etiqueta: "Lun", ent: asig.lun_ent, sal: asig.lun_sal },
        { etiqueta: "Mar", ent: asig.mar_ent, sal: asig.mar_sal },
        { etiqueta: "Mie", ent: asig.mie_ent, sal: asig.mie_sal },
        { etiqueta: "Jue", ent: asig.jue_ent, sal: asig.jue_sal },
        { etiqueta: "Vie", ent: asig.vie_ent, sal: asig.vie_sal },
        { etiqueta: "Sab", ent: asig.sab_ent, sal: asig.sab_sal },
        { etiqueta: "Dom", ent: asig.dom_ent, sal: asig.dom_sal },
      ];
      return dias.filter((d) => d.ent || d.sal);
    },
    []
  );

  const buscarPorPersona = async () => {
    if (!legajo) {
      present({ message: "Ingrese legajo", duration: 3000, color: "warning" });
      return;
    }
    setCargandoPersona(true);
    try {
      const res = await asignacionesCapacitador.asignacionesPorPersona(legajo.trim());
      setAsignacionesPersona(res.data?.datos || []);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudieron obtener las asignaciones",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setCargandoPersona(false);
    }
  };

  const renderTablaAsignaciones = (lista: Asignacion[]) => {
    if (!lista || lista.length === 0) return null;
    const celda = (ent?: string, sal?: string) => `${ent || "--"} - ${sal || "--"}`;
    const borde = '#7a0c2e'; // tono bordo usado en la app
    const headerBg = '#7a0c2e';
    const headerColor = '#fff';
    return (
      <div style={{ overflowX: 'auto', marginTop: 8 }}>
        <table style={{ minWidth: '1100px', width: '100%', borderCollapse: 'collapse', fontSize: '14px', border: `1px solid ${borde}` }}>
          <thead>
            <tr style={{ background: headerBg, color: headerColor }}>
              <th style={{ textAlign: 'left', padding: '8px', border: `1px solid ${borde}` }}>Operario</th>
              <th style={{ textAlign: 'left', padding: '8px', border: `1px solid ${borde}` }}>Legajo</th>
              <th style={{ textAlign: 'left', padding: '8px', border: `1px solid ${borde}` }}>CI</th>
              <th style={{ textAlign: 'left', padding: '8px', border: `1px solid ${borde}` }}>Punto de Servicio</th>
              <th style={{ textAlign: 'left', padding: '8px', border: `1px solid ${borde}` }}>Inicio</th>
              <th style={{ textAlign: 'left', padding: '8px', border: `1px solid ${borde}` }}>Estado</th>
              <th style={{ textAlign: 'center', padding: '8px', border: `1px solid ${borde}` }}>Lun</th>
              <th style={{ textAlign: 'center', padding: '8px', border: `1px solid ${borde}` }}>Mar</th>
              <th style={{ textAlign: 'center', padding: '8px', border: `1px solid ${borde}` }}>Mie</th>
              <th style={{ textAlign: 'center', padding: '8px', border: `1px solid ${borde}` }}>Jue</th>
              <th style={{ textAlign: 'center', padding: '8px', border: `1px solid ${borde}` }}>Vie</th>
              <th style={{ textAlign: 'center', padding: '8px', border: `1px solid ${borde}` }}>Sab</th>
              <th style={{ textAlign: 'center', padding: '8px', border: `1px solid ${borde}` }}>Dom</th>
              <th style={{ textAlign: 'center', padding: '8px', border: `1px solid ${borde}` }}>Total Horas</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((asig) => (
              <tr key={`${asig.id}-${asig.ps_id}`} style={{ borderBottom: `1px solid ${borde}` }}>
                <td style={{ padding: '6px 8px', border: `1px solid ${borde}` }}>{asig.operario}</td>
                <td style={{ padding: '6px 8px', border: `1px solid ${borde}` }}>{asig.legajo || '-'}</td>
                <td style={{ padding: '6px 8px', border: `1px solid ${borde}` }}>{asig.cedula || '-'}</td>
                <td style={{ padding: '6px 8px', border: `1px solid ${borde}` }}>{asig.ps_nombre} ({asig.ps_codigo})</td>
                <td style={{ padding: '6px 8px', border: `1px solid ${borde}` }}>{asig.fecha_inicio || '-'}</td>
                <td style={{ padding: '6px 8px', border: `1px solid ${borde}` }}>{asig.estado}</td>
                <td style={{ padding: '6px 4px', textAlign: 'center', border: `1px solid ${borde}` }}>{celda(asig.lun_ent, asig.lun_sal)}</td>
                <td style={{ padding: '6px 4px', textAlign: 'center', border: `1px solid ${borde}` }}>{celda(asig.mar_ent, asig.mar_sal)}</td>
                <td style={{ padding: '6px 4px', textAlign: 'center', border: `1px solid ${borde}` }}>{celda(asig.mie_ent, asig.mie_sal)}</td>
                <td style={{ padding: '6px 4px', textAlign: 'center', border: `1px solid ${borde}` }}>{celda(asig.jue_ent, asig.jue_sal)}</td>
                <td style={{ padding: '6px 4px', textAlign: 'center', border: `1px solid ${borde}` }}>{celda(asig.vie_ent, asig.vie_sal)}</td>
                <td style={{ padding: '6px 4px', textAlign: 'center', border: `1px solid ${borde}` }}>{celda(asig.sab_ent, asig.sab_sal)}</td>
                <td style={{ padding: '6px 4px', textAlign: 'center', border: `1px solid ${borde}` }}>{celda(asig.dom_ent, asig.dom_sal)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600, border: `1px solid ${borde}` }}>{asig.total_horas || '0:00'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Los resultados salen solo cuando se escribe: la lista no queda fija en
  // pantalla. Los PS de la estructura del jefe vienen primero desde el backend,
  // asi que dentro de los resultados aparecen arriba.
  const puntosFiltrados = useMemo(() => {
    const filtro = filtroPs.trim().toLowerCase();
    if (!filtro) return [];
    return puntos.filter(
      (p) =>
        (p.ps_nombre || '').toLowerCase().includes(filtro) ||
        (p.ps_codigo || '').toLowerCase().includes(filtro)
    );
  }, [puntos, filtroPs]);

  const seleccionarPunto = async (ps: PuntoServicio) => {
    // Al elegir se oculta el buscador y la lista: en pantalla queda el PS
    // elegido y su cuadro de asignaciones.
    setPsElegido(ps);
    setFiltroPs("");
    setAsignacionesPs([]);
    setCargandoPs(true);
    try {
      const res = await asignacionesCapacitador.asignacionesPorPunto(String(ps.id));
      setAsignacionesPs(res.data?.datos || []);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudieron obtener las asignaciones",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setCargandoPs(false);
    }
  };

  const cambiarPunto = () => {
    setPsElegido(null);
    setAsignacionesPs([]);
    setFiltroPs("");
  };

  return (
    <IonPage>
      <Header titulo="Asignaciones" />
      <IonContent id="asignaciones-capacitador" className="ion-padding">
        <IonSegment value={segmento} onIonChange={(e) => setSegmento(e.detail.value as "ps" | "persona")}>
          <IonSegmentButton value="ps">
            <IonLabel>Por Punto de Servicio</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="persona">
            <IonLabel>Por Persona</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        {segmento === "ps" && (
          <>
            {/* Sin PS elegido: solo el buscador. Los resultados aparecen
                unicamente mientras se escribe, no como lista fija. */}
            {!psElegido && (
              <>
                <IonItem>
                  <IonLabel position="stacked">Buscar Punto de Servicio</IonLabel>
                  <IonInput
                    value={filtroPs}
                    disabled={cargandoPuntos}
                    placeholder="Escriba nombre o código"
                    onIonChange={(e) => setFiltroPs(e.detail.value || "")}
                  />
                </IonItem>

                {cargandoPuntos && (
                  <IonItem lines="none">
                    <IonSpinner slot="start" />
                    <IonLabel>Cargando puntos de servicio...</IonLabel>
                  </IonItem>
                )}

                {!cargandoPuntos && filtroPs.trim().length > 0 && (
                  <IonList>
                    {puntosFiltrados.map((ps) => (
                      <IonItem
                        key={ps.id}
                        button
                        onClick={() => seleccionarPunto(ps)}
                        disabled={cargandoPs}
                      >
                        <IonLabel>
                          <strong>{ps.ps_nombre}</strong>
                          {ps.mi_estructura && (
                            <span
                              style={{
                                marginLeft: 8,
                                padding: '2px 6px',
                                borderRadius: 4,
                                fontSize: '0.72em',
                                fontWeight: 600,
                                color: '#fff',
                                background: 'var(--garnet)',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Mi estructura
                            </span>
                          )}
                          <div>{ps.ps_codigo}</div>
                        </IonLabel>
                      </IonItem>
                    ))}
                    {puntosFiltrados.length === 0 && (
                      <IonItem lines="none">
                        <IonLabel>No hay puntos de servicio con ese filtro.</IonLabel>
                      </IonItem>
                    )}
                  </IonList>
                )}
              </>
            )}

            {/* Con PS elegido: se muestra cual es (con opcion de cambiarlo) y su cuadro. */}
            {psElegido && (
              <>
                <IonItem lines="full">
                  <IonLabel>
                    <small style={{ color: 'var(--grey)' }}>Punto de servicio</small>
                    <div>
                      <strong>{psElegido.ps_nombre}</strong>
                      {psElegido.mi_estructura && (
                        <span
                          style={{
                            marginLeft: 8,
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: '0.72em',
                            fontWeight: 600,
                            color: '#fff',
                            background: 'var(--garnet)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Mi estructura
                        </span>
                      )}
                    </div>
                    <div>{psElegido.ps_codigo}</div>
                  </IonLabel>
                  {cargandoPs ? (
                    <IonSpinner slot="end" />
                  ) : (
                    <IonButton slot="end" fill="outline" size="small" onClick={cambiarPunto}>
                      Cambiar
                    </IonButton>
                  )}
                </IonItem>

                {!cargandoPs && asignacionesPs.length === 0 && (
                  <IonItem lines="none">
                    <IonLabel>No hay asignaciones para el punto seleccionado.</IonLabel>
                  </IonItem>
                )}

                {renderTablaAsignaciones(asignacionesPs)}
              </>
            )}
          </>
        )}

        {segmento === "persona" && (
          <>
            <IonItem>
              <IonLabel position="stacked">Legajo</IonLabel>
              <IonInput
                value={legajo}
                disabled={cargandoPersona}
                placeholder="Ej: 12345"
                onIonChange={(e) => setLegajo(e.detail.value || "")}
              />
            </IonItem>
            <IonButton expand="block" className="ion-margin-top" onClick={buscarPorPersona} disabled={cargandoPersona}>
              {cargandoPersona ? <IonSpinner name="crescent" /> : "Ver asignaciones"}
            </IonButton>

            {!cargandoPersona && asignacionesPersona.length === 0 && (
              <IonItem lines="none">
                <IonLabel>No hay asignaciones para la persona.</IonLabel>
              </IonItem>
            )}

            {renderTablaAsignaciones(asignacionesPersona)}
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default AsignacionesCapacitador;
